import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconSettings, Tooltip } from '@salesforce/design-system-react';
import { CQAppConstant } from 'app-constants';

/**
 * Props for the CQManagedPackageInterstitial component.
 */
interface CQManagedPackageInterstitialProps {
    /** Callback when "Install Now" button is clicked */
    onInstallNow: () => void;
    
    /** Callback when "Already installed - Continue" button is clicked */
    onContinue: () => void;
    
    /** Callback when "Back" link is clicked */
    onBack: () => void;
    
    /** Controls visibility of the interstitial */
    isVisible: boolean;
}

function CQManagedPackageInterstitial({
    onInstallNow,
    onContinue,
    onBack,
    isVisible
}: CQManagedPackageInterstitialProps) {
    const { t } = useTranslation();

    if (!isVisible) {
        return null;
    }

    return (
        <IconSettings iconPath="/assets/icons">
            <div className="slds-grid slds-grid_align-center">
                <div className="slds-col slds-size_1-of-1 slds-medium-size_2-of-3 slds-large-size_7-of-8">
                    <div className="slds-box slds-theme_default slds-m-bottom_medium">
                        <div className="slds-grid slds-grid_vertical">
                            <div className="slds-text-heading_small slds-m-bottom_small">
                                <strong>{t('loginpage_install_managed_package_title')}</strong>
                            </div>

                            <div className="slds-notify slds-notify_alert slds-theme_alert-texture slds-theme_warning slds-m-bottom_medium">
                                <span className="slds-icon-utility-warning slds-m-right_x-small">
                                    <svg className="slds-icon slds-icon_x-small" aria-hidden="true">
                                        <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#warning"></use>
                                    </svg>
                                </span>
                                <div className="slds-notify__content">
                                    <p className="slds-text-body_small slds-text-align_left">{t('loginpage_install_managed_package_info')}</p>
                                </div>
                                <Tooltip
                                    id="install-managed-package-tooltip"
                                    content={t('loginpage_install_managed_package_tooltip')}
                                    align="left"
                                >
                                    <button
                                        type="button"
                                        className="slds-button slds-button_icon slds-button_icon-small slds-m-left_x-small"
                                        title="Info"
                                    >
                                        <svg className="slds-button__icon" aria-hidden="true">
                                            <use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#info"></use>
                                        </svg>
                                    </button>
                                </Tooltip>
                            </div>

                            <div className="slds-box slds-m-bottom_medium">
                                <div className="slds-grid slds-grid_align-spread slds-grid_vertical-align-center">
                                    <div className="slds-grid slds-grid_vertical-align-center">
                                        <span className="slds-icon_container slds-icon-standard-apps slds-m-right_small" title="Package">
                                            <svg className="slds-icon slds-icon_small" aria-hidden="true">
                                                <use xlinkHref="/assets/icons/standard-sprite/svg/symbols.svg#apps"></use>
                                            </svg>
                                        </span>
                                        <div className="slds-grid slds-grid_vertical">
                                            <span className="slds-text-title_bold">{t('loginpage_install_managed_package_row_label')}</span>
                                            <span className="slds-text-body_small slds-text-color_weak">{t('loginpage_install_managed_package_row_subtitle')}</span>
                                        </div>
                                    </div>

                                    <button className="slds-button slds-button_brand" onClick={onInstallNow}>
                                        {t('loginpage_install_managed_package_install_now')}
                                    </button>
                                </div>
                            </div>

                            <div className="slds-text-align_center">
                                <button
                                    className="slds-button slds-button_outline-brand"
                                    onClick={onContinue}
                                >
                                    {t('loginpage_install_managed_package_continue')}
                                </button>
                            </div>

                            <div className="slds-text-align_center slds-m-top_small">
                                <button className="slds-button slds-button_link" onClick={onBack}>
                                    {t('loginpage_install_managed_package_back')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </IconSettings>
    );
}

export default CQManagedPackageInterstitial;
